/**
 * Stock Report Filters Component
 * @see US-16.5 - UI Rapport Principal
 */

import { useMemo, useState } from 'react';
import { Select, DatePicker, SearchAutocomplete } from '@/components/ui';
import type { DateRange, SearchAutocompleteOption } from '@/components/ui';
import { useFlatCategories } from '@/features/raw-products/hooks/useCategories';
import { useRawProducts, useRawProduct } from '@/features/raw-products/hooks/useRawProducts';
import { useDebounce } from '@/hooks';
import { MarketTypeLabels } from '@bstock/shared';

interface StockReportFiltersProps {
  dateRange: DateRange;
  categoryId: string;
  marketType: string;
  rawProductId: string;
  bulkFilter: string;
  onDateRangeChange: (range: DateRange) => void;
  onCategoryChange: (value: string) => void;
  onMarketTypeChange: (value: string) => void;
  onRawProductChange: (value: string) => void;
  onBulkFilterChange: (value: string) => void;
}

export function StockReportFilters({
  dateRange,
  categoryId,
  marketType,
  rawProductId,
  bulkFilter,
  onDateRangeChange,
  onCategoryChange,
  onMarketTypeChange,
  onRawProductChange,
  onBulkFilterChange,
}: StockReportFiltersProps) {
  // Fetch categories for filter
  const { data: categories = [] } = useFlatCategories();

  // Search state for autocomplete with debounce
  const [productSearch, setProductSearch] = useState('');
  const debouncedSearch = useDebounce(productSearch, 300);

  // Fetch raw products with server-side search (only when search >= 2 chars)
  const shouldSearch = debouncedSearch.length >= 2;
  const { data: productsData, isLoading: isLoadingProducts } = useRawProducts(
    {
      search: debouncedSearch,
      isActive: true,
      limit: 50,
      sortBy: 'name',
      sortOrder: 'asc',
    },
    { enabled: shouldSearch }
  );

  // Build autocomplete options from search results
  const productOptions = useMemo<SearchAutocompleteOption[]>(() => {
    if (!shouldSearch) return [];
    const products = productsData?.data ?? [];
    return products.map((p) => ({
      id: p.id,
      label: p.name,
      sublabel: p.sku || undefined,
    }));
  }, [productsData?.data, shouldSearch]);

  // Fetch selected product by ID (for deep linking display)
  const { data: selectedProductData } = useRawProduct(rawProductId || null);

  // Get selected product for display
  const selectedProduct = useMemo<SearchAutocompleteOption | null>(() => {
    if (!rawProductId) return null;
    // First check if it's in current search results
    const fromOptions = productOptions.find((p) => p.id === rawProductId);
    if (fromOptions) return fromOptions;
    // Otherwise use the fetched product data
    if (selectedProductData) {
      return {
        id: selectedProductData.id,
        label: selectedProductData.name,
        sublabel: selectedProductData.sku || undefined,
      };
    }
    return null;
  }, [rawProductId, productOptions, selectedProductData]);

  // Category options
  const categoryOptions = [
    { value: '', label: 'Toutes les categories' },
    ...categories.map((c: { id: string; name: string }) => ({ value: c.id, label: c.name })),
  ];

  // Market type options
  const marketTypeOptions = [
    { value: '', label: 'Tous les types' },
    ...Object.entries(MarketTypeLabels).map(([value, label]) => ({
      value,
      label,
    })),
  ];

  // Bulk filter options
  const bulkFilterOptions = [
    { value: '', label: 'Tous les produits' },
    { value: 'bulk', label: 'Bulk uniquement' },
    { value: 'non-bulk', label: 'Non-bulk uniquement' },
  ];

  return (
    <div className="flex flex-wrap items-end gap-4">
      {/* Date range picker */}
      <div className="w-80">
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          Periode
        </label>
        <DatePicker
          maxDate={new Date()}
          value={dateRange}
          onChange={onDateRangeChange}
          placeholder="Selectionner une periode"
          showTime
        />
      </div>

      {/* Category filter */}
      <div className="w-48">
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          Categorie
        </label>
        <Select
          value={categoryId}
          onChange={onCategoryChange}
          options={categoryOptions}
          placeholder="Categorie"
          allowEmpty={false}
        />
      </div>

      {/* Market type filter */}
      <div className="w-40">
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          Type
        </label>
        <Select
          value={marketType}
          onChange={onMarketTypeChange}
          options={marketTypeOptions}
          placeholder="Type"
          allowEmpty={false}
        />
      </div>

      {/* Bulk filter */}
      <div className="w-44">
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          Classification
        </label>
        <Select
          value={bulkFilter}
          onChange={onBulkFilterChange}
          options={bulkFilterOptions}
          placeholder="Classification"
          allowEmpty={false}
        />
      </div>

      {/* Raw product filter */}
      <div className="w-64">
        <SearchAutocomplete
          label="Produit"
          options={productOptions}
          value={selectedProduct}
          onChange={(option) => onRawProductChange(option?.id || '')}
          searchValue={productSearch}
          onSearchChange={setProductSearch}
          placeholder="Rechercher un produit..."
          minSearchLength={2}
          isLoading={isLoadingProducts}
          emptyMessage="Aucun produit trouve"
        />
      </div>
    </div>
  );
}
